/**
 * The .h2fig container, defined once.
 *
 * The encoder and the decoder each carried their own copy of the magic header, the extension id,
 * the key derivation and the XOR — four pieces of one wire format, written twice. They agreed,
 * which is the point: nothing enforced that they would go on agreeing, and a change to either
 * side produces files the other half silently mis-reads rather than rejects. The same argument
 * already moved their two hand-rolled SHA-256 implementations onto one published call.
 *
 * The format:
 *
 *   [10-byte magic] [UTF-8 JSON envelope]
 *   envelope = { signature: sha256hex(payloadJson),
 *                keyHint:   'YYYY-MM-DD',
 *                data:      xor(payloadJson, keyFor(keyHint)),
 *                format:    'h2fig' }
 *
 * This is obfuscation, not encryption: the key is derived from a date stored in the file itself.
 * It exists to stop a .h2fig being casually read or hand-edited, and it is not a security
 * boundary — see the note on the integrity check in decryption.ts.
 */

/** `.H2FIG\r\n\x1a\n` — the same shape as PNG's, so a text-mode transfer corrupts it visibly. */
export const H2FIG_HEADER = new Uint8Array([
  0x89, 0x48, 0x32, 0x46, 0x49, 0x47, 0x0d, 0x0a, 0x1a, 0x0a,
]);

const EXTENSION_ID = "html-tofig-design";

/** The XOR key for a given keyHint date. Both halves must derive it identically. */
export function keyFor(keyHint: string): string {
  return `${EXTENSION_ID}-${keyHint}-tofig`;
}

/**
 * XOR a binary string against a repeating key. Its own inverse, which is why one function serves
 * both directions.
 *
 * An empty key would return the input untouched — silently producing a "decrypted" payload that
 * is still the ciphertext — so it throws instead. `keyFor` cannot produce one, but the throw is
 * what makes that true rather than merely likely.
 */
export function xorTransform(text: string, key: string): string {
  if (!key) throw new Error("h2fig: XOR called with an empty key");
  let result = "";
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
}
